import { useEffect, useRef, useState } from 'react';
import { ncfAPI } from '../../services/api';
import { describeAddressIssue, validateAddress } from '../../utils/addressValidation.js';

function AddressInput({ id, value, onChange, onBlur, disabled = false, className, fromJobDiva = false, required = true, multiline = false }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [validation, setValidation] = useState(null);
  const containerRef = useRef(null);
  const typedRef = useRef(false);
  const text = value == null ? '' : String(value);

  useEffect(() => {
    let active = true;
    const timer = setTimeout(() => {
      validateAddress(text).then(result => { if (active) setValidation(result); });
    }, typedRef.current ? 700 : 0);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [text]);

  useEffect(() => {
    const query = text.trim();
    if (!typedRef.current || query.length < 3) {
      setSuggestions([]);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const response = await ncfAPI.suggestAddresses(query, { signal: controller.signal });
        setSuggestions(response.data.suggestions || []);
        setHighlighted(-1);
      } catch (_error) {
        if (!controller.signal.aborted) setSuggestions([]);
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [text]);

  useEffect(() => {
    const close = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const choose = (suggestion) => {
    typedRef.current = false;
    setOpen(false);
    setSuggestions([]);
    onChange(suggestion.label);
  };

  const handleKeyDown = (event) => {
    // Addresses are stored on one line; the textarea only wraps them for readability.
    if (multiline && event.key === 'Enter' && !(open && highlighted >= 0)) {
      event.preventDefault();
      return;
    }
    if (!open || suggestions.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlighted(previous => (previous + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted(previous => (previous <= 0 ? suggestions.length - 1 : previous - 1));
    } else if (event.key === 'Enter' && highlighted >= 0) {
      event.preventDefault();
      choose(suggestions[highlighted]);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const issue = required ? describeAddressIssue(validation) : '';
  const status = !text.trim() ? null : issue
    ? <span className="ncf-address-status is-incomplete">{fromJobDiva ? `From JobDiva — ${issue.charAt(0).toLowerCase()}${issue.slice(1)} Please complete it.` : issue}</span>
    : validation?.complete ? <span className="ncf-address-status is-complete">{validation.verified ? 'Verified address' : 'Address looks complete'}</span> : null;

  const inputProps = {
    id,
    className: [className, issue && 'ncf-address-incomplete'].filter(Boolean).join(' ') || undefined,
    value: text,
    autoComplete: 'off',
    disabled,
    'aria-invalid': Boolean(issue),
    onChange: event => { typedRef.current = true; setOpen(true); onChange(event.target.value.replace(/\s*\n\s*/g, ' ')); },
    onFocus: () => setOpen(true),
    onBlur,
    onKeyDown: handleKeyDown,
    placeholder: 'Street number and name, suite, city, state/province, postal code, country'
  };

  return <span className="typeahead ncf-address-input" ref={containerRef}>
    {multiline ? <textarea {...inputProps} rows={3} /> : <input type="text" {...inputProps} />}
    {open && !disabled && suggestions.length > 0 && <span className="typeahead-dropdown">
      {suggestions.map((suggestion, index) => <button
        type="button"
        key={suggestion.id}
        className={`typeahead-option${index === highlighted ? ' is-highlighted' : ''}`}
        onMouseEnter={() => setHighlighted(index)}
        onMouseDown={event => event.preventDefault()}
        onClick={() => choose(suggestion)}
      >
        <span className="typeahead-option-label">{suggestion.label}</span>
        {suggestion.sublabel && <span className="typeahead-option-sublabel">{suggestion.sublabel}</span>}
      </button>)}
    </span>}
    {status}
  </span>;
}

export default AddressInput;
